import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  FaPlus,
  FaTrash,
  FaCheck,
  FaTimes,
  FaPaperclip,
  FaFileExcel,
  FaEdit,
  FaExternalLinkAlt,
} from "react-icons/fa";
import api from "../../../libs/axios";
import { useAuth } from "../../../provider/auth.provider";
import { useConfirm } from "../../../provider/confirm.provider";
import { ExcelDownload } from "../../../utils/excel.utils";

// Same six money buckets as a general claim — INC claims only differ by being
// tied to a site and, optionally, to a single visit on that site.
const AMOUNT_FIELDS = [
  { key: "travel_amount", label: "Travel" },
  { key: "da_amount", label: "DA" },
  { key: "stay_amount", label: "Stay" },
  { key: "food_amount", label: "Food" },
] as const;

const EMPTY_FORM = {
  claim_title: "",
  claim_description: "",
  claim_date: "",
  visit_id: "",
  travel_amount: "",
  da_amount: "",
  stay_amount: "",
  food_amount: "",
  other_amount1: "",
  other_description1: "",
  other_amount2: "",
  other_description2: "",
};

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-gray-100 text-gray-700",
  submitted: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

const num = (v: any) => Number(v || 0);
const money = (v: any) => num(v).toFixed(2);
const dateText = (v: any) => (v ? new Date(v).toLocaleDateString() : "-");
const fullName = (u: any) => (u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username : "-");

interface ClaimsTabProps {
  siteId: string;
  // True once billing has been finalized. Claims on a locked site are
  // read-only — the API refuses to raise, edit, approve or delete them.
  locked?: boolean;
}

const ClaimsTab: React.FC<ClaimsTabProps> = ({ siteId, locked = false }) => {
  const { user, hasPermission } = useAuth();
  const { confirm } = useConfirm();
  // Viewing survives the lock; raising, editing, approving and deleting do not.
  const canView = hasPermission("IncSite", "claim");
  const canClaim = canView && !locked;
  const canApprove = hasPermission("IncSite", "claim_approve") && !locked;

  const [claims, setClaims] = useState<any[]>([]);
  const [visits, setVisits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [bills, setBills] = useState<File[]>([]);
  const [showExport, setShowExport] = useState(false);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [claimRes, visitRes] = await Promise.all([
        api.get(`/inc/sites/${siteId}/claims`, { params: { limit: 200 } }),
        // Visits power the optional "against visit" picker. A user without the
        // visits permission can still raise a site-level claim.
        api.get(`/inc/sites/${siteId}/visits`).catch(() => ({ data: { data: [] } })),
      ]);
      const body = claimRes.data?.data;
      setClaims(Array.isArray(body) ? body : body?.data || []);
      setVisits(visitRes.data?.data || []);
    } catch {
      toast.error("Failed to load INC claims");
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    load();
  }, [load]);

  const formTotal = useMemo(
    () =>
      num(form.travel_amount) +
      num(form.da_amount) +
      num(form.stay_amount) +
      num(form.food_amount) +
      num(form.other_amount1) +
      num(form.other_amount2),
    [form],
  );

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setBills([]);
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (claim: any) => {
    setForm({
      claim_title: claim.claim_title || "",
      claim_description: claim.claim_description || "",
      claim_date: claim.claim_date ? String(claim.claim_date).slice(0, 10) : "",
      visit_id: claim.visit_id ? String(claim.visit_id) : "",
      travel_amount: String(claim.travel_amount ?? ""),
      da_amount: String(claim.da_amount ?? ""),
      stay_amount: String(claim.stay_amount ?? ""),
      food_amount: String(claim.food_amount ?? ""),
      other_amount1: String(claim.other_amount1 ?? ""),
      other_description1: claim.other_description1 || "",
      other_amount2: String(claim.other_amount2 ?? ""),
      other_description2: claim.other_description2 || "",
    });
    setBills([]);
    setEditingId(claim.id);
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.claim_title.trim()) return toast.error("Claim title is required");
    if (!form.claim_description.trim()) return toast.error("Claim description is required");
    if (formTotal <= 0) return toast.error("Enter at least one amount");

    setSaving(true);
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        // visit_id is optional — omitting it makes this a site-level claim.
        if (key === "visit_id" && !value) return;
        payload.append(key, value ?? "");
      });
      bills.forEach((file) => payload.append("bills", file));

      if (editingId) {
        await api.put(`/inc/claims/${editingId}`, payload);
        toast.success("Claim updated");
      } else {
        await api.post(`/inc/sites/${siteId}/claims`, payload);
        toast.success("Claim submitted");
      }
      resetForm();
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to save claim");
    } finally {
      setSaving(false);
    }
  };

  const approve = async (id: number) => {
    try {
      await api.put(`/inc/claims/${id}/approve-reject`, { status: "approved" });
      toast.success("Claim approved");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to approve claim");
    }
  };

  const reject = async (id: number) => {
    if (!rejectReason.trim()) return toast.error("Enter a rejection reason");
    try {
      await api.put(`/inc/claims/${id}/approve-reject`, { status: "rejected", rejection_reason: rejectReason.trim() });
      toast.success("Claim rejected");
      setRejectingId(null);
      setRejectReason("");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to reject claim");
    }
  };

  const remove = async (id: number) => {
    const ok = await confirm({
      title: "Delete claim",
      message: "Delete this claim? Its uploaded bills are removed with it and this cannot be undone.",
      confirmText: "Delete claim",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/inc/claims/${id}`);
      toast.success("Claim deleted");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to delete claim");
    }
  };

  const visitLabel = (visitId: any) => {
    if (!visitId) return "Site-level";
    const visit = visits.find((v) => Number(v.id) === Number(visitId));
    return visit
      ? `Visit #${visit.visit_number}${visit.visit_date ? ` · ${new Date(visit.visit_date).toLocaleDateString()}` : ""}`
      : `Visit #${visitId}`;
  };

  const exportRows = useMemo(
    () =>
      claims.map((c) => ({
        "Claim ID": c.id,
        Visit: c.visit_id ? visitLabel(c.visit_id) : "Site-level",
        Title: c.claim_title,
        Description: c.claim_description,
        "Claim Date": dateText(c.claim_date),
        "Raised By": fullName(c.staff),
        Travel: num(c.travel_amount),
        DA: num(c.da_amount),
        Stay: num(c.stay_amount),
        Food: num(c.food_amount),
        "Other 1": num(c.other_amount1),
        "Other 1 Description": c.other_description1 || "",
        "Other 2": num(c.other_amount2),
        "Other 2 Description": c.other_description2 || "",
        "Submitted Total": num(c.submitted_total),
        "Approved Total": num(c.final_approved_total),
        Status: c.status,
        "Approved By": fullName(c.approver),
        "Rejection Reason": c.rejection_reason || "",
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [claims, visits],
  );

  const totals = useMemo(
    () =>
      claims.reduce(
        (acc: { submitted: number; approved: number }, c: any) => ({
          submitted: acc.submitted + num(c.submitted_total),
          approved: acc.approved + num(c.final_approved_total),
        }),
        { submitted: 0, approved: 0 },
      ),
    [claims],
  );

  const inp = "px-2 py-2 text-sm border border-gray-300 rounded w-full";
  const lbl = "block text-xs text-gray-500 mb-1";

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;

  return (
    <div>
      {locked && (
        <p className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-3 py-2 mb-3">
          Billing has been finalized for this site — it is locked and this tab is read-only.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-xs text-gray-400">
          INC claims are separate from Expense claims — they only appear here, and may be raised against the whole site
          or a single visit.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => (exportRows.length ? setShowExport(true) : toast.error("No claims to export"))}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700"
          >
            <FaFileExcel className="w-4 h-4" /> Export Excel
          </button>
          {canClaim && !showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-800 rounded hover:bg-blue-900"
            >
              <FaPlus className="w-3 h-3" /> New Claim
            </button>
          )}
        </div>
      </div>

      {canClaim && showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">{editingId ? "Edit Claim" : "New INC Claim"}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2">
              <label className={lbl}>Claim Title <span className="text-red-500">*</span></label>
              <input value={form.claim_title} onChange={(e) => setForm({ ...form, claim_title: e.target.value })} className={inp} placeholder="e.g. Site travel and stay" />
            </div>
            <div>
              <label className={lbl}>Claim Date</label>
              <input type="date" value={form.claim_date} onChange={(e) => setForm({ ...form, claim_date: e.target.value })} className={inp} />
            </div>
            <div>
              <label className={lbl}>Against Visit (optional)</label>
              <select value={form.visit_id} onChange={(e) => setForm({ ...form, visit_id: e.target.value })} className={inp}>
                <option value="">Whole site (no visit)</option>
                {visits.map((v) => (
                  <option key={v.id} value={v.id}>
                    Visit #{v.visit_number}
                    {v.visit_date ? ` · ${new Date(v.visit_date).toLocaleDateString()}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 lg:col-span-4">
              <label className={lbl}>Description <span className="text-red-500">*</span></label>
              <textarea
                value={form.claim_description}
                onChange={(e) => setForm({ ...form, claim_description: e.target.value })}
                rows={2}
                className={`${inp} resize-none`}
                placeholder="What is being claimed and why"
              />
            </div>

            {AMOUNT_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <label className={lbl}>{label} Amount</label>
                <input
                  value={(form as any)[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className={inp}
                  placeholder="0"
                />
              </div>
            ))}

            <div>
              <label className={lbl}>Other Amount 1</label>
              <input value={form.other_amount1} onChange={(e) => setForm({ ...form, other_amount1: e.target.value })} className={inp} placeholder="0" />
            </div>
            <div>
              <label className={lbl}>Other 1 Description</label>
              <input value={form.other_description1} onChange={(e) => setForm({ ...form, other_description1: e.target.value })} className={inp} />
            </div>
            <div>
              <label className={lbl}>Other Amount 2</label>
              <input value={form.other_amount2} onChange={(e) => setForm({ ...form, other_amount2: e.target.value })} className={inp} placeholder="0" />
            </div>
            <div>
              <label className={lbl}>Other 2 Description</label>
              <input value={form.other_description2} onChange={(e) => setForm({ ...form, other_description2: e.target.value })} className={inp} />
            </div>

            <div>
              <label className={lbl}>Total</label>
              <input value={money(formTotal)} readOnly className={`${inp} bg-gray-100 text-gray-600 font-medium`} />
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <label className={lbl}>Bill Attachments</label>
              <input
                type="file"
                multiple
                onChange={(e) => setBills(Array.from(e.target.files || []))}
                className="text-xs py-2"
              />
              {editingId && <p className="text-xs text-gray-400 mt-1">New files are added to the existing bills.</p>}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-800 rounded hover:bg-blue-900 disabled:bg-blue-400"
            >
              {saving ? "Saving..." : editingId ? "Save Changes" : "Submit Claim"}
            </button>
            <button onClick={resetForm} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {claims.length === 0 ? (
        <p className="text-sm text-gray-500">No claims raised for this site.</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left whitespace-nowrap">Claim</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Visit</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Date</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Raised By</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Submitted</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Approved</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Bills</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {claims.map((c) => {
                const isMine = Number(c.staff_id) === Number(user?.id);
                const isOpen = c.status === "submitted" || c.status === "pending";
                return (
                  <React.Fragment key={c.id}>
                    <tr>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-800">{c.claim_title}</div>
                        <div className="text-xs text-gray-500 break-words max-w-xs">{c.claim_description}</div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{visitLabel(c.visit_id)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{dateText(c.claim_date)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fullName(c.staff)}</td>
                      <td className="px-3 py-2 text-right">{money(c.submitted_total)}</td>
                      <td className="px-3 py-2 text-right font-medium">
                        {c.status === "approved" ? money(c.final_approved_total) : "-"}
                      </td>
                      <td className="px-3 py-2">
                        {(c.bill_attachments || []).length === 0 ? (
                          <span className="text-xs text-gray-400">None</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(c.bill_attachments || []).map((url: string, i: number) => (
                              <a
                                key={i}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                                title={`Bill ${i + 1}`}
                              >
                                <FaPaperclip className="w-2.5 h-2.5" />
                                {i + 1}
                                <FaExternalLinkAlt className="w-2 h-2" />
                              </a>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${STATUS_BADGE[c.status] || "bg-gray-100 text-gray-600"}`}>
                          {c.status}
                        </span>
                        {c.status === "rejected" && c.rejection_reason && (
                          <div className="text-xs text-red-600 mt-1 max-w-[180px] break-words">{c.rejection_reason}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-2">
                          {canApprove && c.status === "submitted" && !isMine && (
                            <>
                              <button onClick={() => approve(c.id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Approve">
                                <FaCheck className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => { setRejectingId(rejectingId === c.id ? null : c.id); setRejectReason(""); }}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                title="Reject"
                              >
                                <FaTimes className="w-3 h-3" />
                              </button>
                            </>
                          )}
                          {isMine && isOpen && canClaim && (
                            <button onClick={() => startEdit(c)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                              <FaEdit className="w-3 h-3" />
                            </button>
                          )}
                          {isMine && c.status !== "approved" && canClaim && (
                            <button onClick={() => remove(c.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Delete">
                              <FaTrash className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {rejectingId === c.id && (
                      <tr className="bg-red-50">
                        <td colSpan={9} className="px-3 py-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <input
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              placeholder="Reason for rejection"
                              className="flex-1 min-w-[220px] px-2 py-1.5 text-sm border border-red-200 rounded"
                            />
                            <button onClick={() => reject(c.id)} className="px-3 py-1.5 text-sm text-white bg-red-600 rounded hover:bg-red-700">
                              Confirm Reject
                            </button>
                            <button onClick={() => setRejectingId(null)} className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded">
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 font-medium">
              <tr>
                <td className="px-3 py-2" colSpan={4}>Total</td>
                <td className="px-3 py-2 text-right">{totals.submitted.toFixed(2)}</td>
                <td className="px-3 py-2 text-right">{totals.approved.toFixed(2)}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <ExcelDownload
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        data={exportRows}
        filename={`inc_claims_site_${siteId}`}
        title="Export INC Claims"
        description="Download INC claim records for this site"
      />
    </div>
  );
};

export default ClaimsTab;
